import styled from "@emotion/styled";
import { LocalDate } from "@js-joda/core";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import useOpen from "../../hooks/useOpen";
import { isLoading, Loading } from "../../Loading";
import {
  CaregiverImportedVisitDetails,
  CaregiverVisitEditRequest,
  ClockedInStatus,
  ClockInOutCallType,
  ImportedVisitId,
  Month,
  PatientTaskInstance,
  PatientTaskInstanceId,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
  VisitInstanceId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { AppPatientsState } from "../../system/AppState/AppPatientsState";
import { translate } from "../../translate";
import { KeyedList, List } from "../../utils/List";
import {
  filterPatientTask,
  isTaskFixesRequired,
  isTaskScheduledInMonth,
} from "../../utils/taskUtils";
import { CicoModalEntity, filterVisit, getClosestVisit } from "../../utils/visitUtils";
import { AssignedVisitsAndTasksList } from "../AssignedVisitsAndTasksList";
import NavbarActionButton from "../MainV2Screen/components/NavbarActionButton";
import MonthPicker from "../MonthPicker/MonthPicker";
import PatientsFilterBottomSheet from "../PatientsFilterBottomSheet";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import ScaffoldSearchbar from "../ScaffoldSearchbar";
import SearchPatientDialog from "../SearchPatientDialog";
import { UseContactSupportReturnType } from "../useContactSupport";
import useScheduledVisitsAvailableFilters from "./useScheduledVisitsAvailableFilters";
import useScheduledVistsFilters from "./useScheduledVistsFilters";
import { AppButton } from "../Generic/AppButton";
import { default as SearchIconRaw } from "/public/assets/search.svg?react";
import { default as FilterIconRaw } from "/public/assets/sliders.svg?react";

type Props = {
  caregiver: AppCaregiver;
  importedVisits: List<CaregiverImportedVisitDetails>;
  clockedInStatus: ClockedInStatus;
  contactSupportProps: UseContactSupportReturnType;
  patientTasks: List<PatientTaskInstance>;
  rescheduleRequests: List<CaregiverVisitEditRequest>;
  scheduledVisitsParams?: {
    isApplyOnlyTasksWithRequiredFixesFilter?: boolean;
  };
  scheduleYear: number;
  scheduleMonth: Month;
  visitInstances: KeyedList<VisitInstanceDetails, "id">;
  patientsState: Loading | AppPatientsState;
  onClickGoBack: () => void;
  onClickStartNow: (entity: CicoModalEntity) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onRequestAvailableVisits: () => void;
  onRequestNavigate: (page: Page) => void;
  onUploadVerificationSelfie: (params: {
    visitInstanceId: VisitInstanceId;
    imageBase64: string;
    clockType: ClockInOutCallType;
  }) => Promise<void>;
  onClickPreviousMonth: () => void;
  onClickNextMonth: () => void;
  onChangeYearMonth: (year: number, month: number) => void;
  onRequestVisitAdjustmentCheck: API["getCaregiverCanAdjustVisitInstanceTimes"];
  onCheckIfDocumentsSubmitted: API["checkIfAllDocumentsSubmitted"];
  onCaregiverMarkVisitAsMissed: API["caregiverMarkVisitAsMissed"];
};

export default function ScheduledVisitsRNScreen(props: Props) {
  const searchPatientDialog = useOpen();
  const filters = useScheduledVistsFilters({
    defaultFilters: {
      isFixesRequiredOnly: props.scheduledVisitsParams?.isApplyOnlyTasksWithRequiredFixesFilter,
    },
  });
  const availableFilters = useScheduledVisitsAvailableFilters({
    patientTasks: props.patientTasks,
    visitPublicDetails: props.visitInstances.map((visit) => visit.publicVisitDetails),
    patientTaskBroadcasts: List.empty(),
  });

  const patientTasks = React.useMemo(
    () =>
      props.patientTasks.filter((task) =>
        filterPatientTask(task, {
          ...filters,
          scheduleYear: props.scheduleYear,
          scheduleMonth: props.scheduleMonth,
        })
      ),
    [filters, props.patientTasks, props.scheduleMonth, props.scheduleYear]
  );

  const isExistsTaskWithFixesRequired = React.useMemo(() => {
    // show if there are tasks that are scheduled to the current month in the filter and have fixes required
    return patientTasks
      .filter((t) =>
        isTaskScheduledInMonth(t, {
          scheduleMonth: props.scheduleMonth,
          scheduleYear: props.scheduleYear,
        })
      )
      .some((task) => isTaskFixesRequired(task));
  }, [patientTasks, props.scheduleMonth, props.scheduleYear]);

  const visitInstances = React.useMemo(
    () =>
      KeyedList.from(
        props.visitInstances.filter((visit) => filterVisit(visit.publicVisitDetails, filters))
      )("id"),
    [filters, props.visitInstances]
  );
  const patientsState = React.useMemo(() => {
    if (isLoading(props.patientsState)) return props.patientsState;

    const patientIds = new Set(
      props.patientTasks
        .map((task) => task.taskDetails.patientId)
        .concat(props.visitInstances.map((visit) => visit.publicVisitDetails.patientId))
        .toArray()
    );

    return {
      patients: KeyedList.from(
        props.patientsState.patients.filter((patient) => patientIds.has(patient.id))
      )("id"),
    };
  }, [props.patientTasks, props.patientsState, props.visitInstances]);

  const handleClickVisitInstance = (visitInstanceId: VisitInstanceId) => {
    return props.onRequestNavigate({ type: "VisitInstance", visitInstanceId });
  };

  const handleClickImportedVisit = (importedVisitId: ImportedVisitId, patientName: string) => {
    props.onRequestNavigate({
      type: "ConvertImportedVisit",
      importedVisitId: importedVisitId,
      patientName: patientName,
    });
  };

  const handleClickTask = (
    patientTaskInstanceId: PatientTaskInstanceId,
    initialScheduleDateTimePickerOpen: boolean
  ) => {
    props.onRequestNavigate({
      type: "PatientTaskInstance",
      patientTaskInstanceId: patientTaskInstanceId,
      initialScheduleDateTimePickerOpen: initialScheduleDateTimePickerOpen,
    });
  };

  React.useEffect(() => {
    const closestVisit = getClosestVisit(visitInstances);
    const date = closestVisit?.data.startTimeLocal.toLocalDate() ?? LocalDate.now();
    const elem = document.getElementById("day" + date.toString());
    const timeout = window.setTimeout(() => elem?.scrollIntoView({ behavior: "smooth" }), 150);

    return () => window.clearTimeout(timeout);
  }, [visitInstances]);

  return (
    <>
      <Scaffold
        title={<ScaffoldTitle label={translate(STRINGS.MY_VISITS)} />}
        extraHeader={
          <Header>
            <ScaffoldSearchbar
              leading={
                <NavbarActionButton
                  icon={<SearchIcon />}
                  onClick={searchPatientDialog.actions.open}
                />
              }
              trailing={
                <NavbarActionButton
                  icon={<FilterIcon />}
                  badge={filters.count}
                  onClick={filters.open}
                />
              }
              placeholder={filters.patient?.fullName ?? translate(STRINGS.SEARCH_PATIENT)}
              onClick={searchPatientDialog.actions.open}
              onFocus={searchPatientDialog.actions.open}
            />
            <MonthPicker
              year={props.scheduleYear}
              month={props.scheduleMonth}
              onClickPrevious={props.onClickPreviousMonth}
              onClickNext={props.onClickNextMonth}
              onChange={props.onChangeYearMonth}
            />
          </Header>
        }
        onClickGoBack={props.onClickGoBack}
      >
        {isExistsTaskWithFixesRequired && (
          <div style={{ paddingInline: 15, paddingTop: 15 }}>
            <AppButton
              block
              variant={filters.isFixesRequiredOnly ? "filled" : "outline"}
              color={filters.isFixesRequiredOnly ? "primary" : "black"}
              onClick={() => filters.setIsFixesRequiredOnly(!filters.isFixesRequiredOnly)}
            >
              {filters.isFixesRequiredOnly ? (
                <span>{translate(STRINGS.SHOW_ALL_TASKS)}</span>
              ) : (
                <span>{translate(STRINGS.SHOW_TASKS_THAT_REQUIRE_FIXES)}</span>
              )}
            </AppButton>
          </div>
        )}

        <AssignedVisitsAndTasksList
          caregiverId={props.caregiver.id}
          isCaregiverOfMedflyt={props.caregiver.isCaregiverOfMedFlyt}
          importedVisits={props.importedVisits}
          clockedInStatus={props.clockedInStatus}
          contactSupportProps={props.contactSupportProps}
          patientTasksInstances={patientTasks}
          rescheduleRequests={props.rescheduleRequests}
          scheduleView="DATE"
          scheduleViewMonth={props.scheduleMonth}
          scheduleViewYear={props.scheduleYear}
          visitInstances={visitInstances}
          onClickStartNow={props.onClickStartNow}
          onClickTask={handleClickTask}
          onClock={props.onClock}
          onImportedVisitClick={handleClickImportedVisit}
          onRequestAvailableVisits={props.onRequestAvailableVisits}
          onRequestNavigate={props.onRequestNavigate}
          onScheduledVisitClick={handleClickVisitInstance}
          onUploadVerificationSelfie={(visitInstanceId, imageBase64, clockType) =>
            props.onUploadVerificationSelfie({ visitInstanceId, imageBase64, clockType })
          }
          onRequestVisitAdjustmentCheck={props.onRequestVisitAdjustmentCheck}
          onClickNextMonth={props.onClickNextMonth}
          onCheckIfDocumentsSubmitted={props.onCheckIfDocumentsSubmitted}
          onCaregiverMarkVisitAsMissed={props.onCaregiverMarkVisitAsMissed}
        />
        <div style={{ height: 64 }} />
      </Scaffold>
      <SearchPatientDialog
        patientsState={patientsState}
        isOpen={searchPatientDialog.isOpen}
        placeholder={translate(STRINGS.SEARCH_PATIENT)}
        onClickPatient={(patient) => {
          filters.setPatient(patient);
          searchPatientDialog.actions.dismiss();
        }}
        onRequestClose={() => {
          searchPatientDialog.actions.dismiss();
          filters.setPatient(null);
        }}
      />
      <PatientsFilterBottomSheet
        isOpen={filters.isOpen}
        availablePatientSchedule={availableFilters.patientSchedule}
        availableBoroughs={availableFilters.boroughs}
        availableVisitTypes={availableFilters.visitTypes}
        boroughs={filters.boroughs}
        patientSchedule={filters.patientSchedule}
        visitTypes={filters.visitTypes}
        onChangeBoroughs={filters.setBoroughs}
        onChangePatientSchedule={filters.setPatientSchedule}
        onChangeVisitTypes={filters.setVisitTypes}
        onRequestClose={filters.close}
      />
    </>
  );
}

export const SearchIcon = styled(SearchIconRaw)`
  color: var(--tw-slate-700);
`;

const FilterIcon = styled(FilterIconRaw)`
  color: var(--tw-slate-700);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
